import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { FiArrowLeft, FiEdit2, FiPlus, FiTrash2, FiTruck } from 'react-icons/fi';
import api from '../config/api';
import { usePermissions } from '../context/PermissionsContext';
import { FadeIn } from '../components/motion';
import SensitiveField from '../components/erp/SensitiveField';
import {
  EmptyState as ErpEmpty,
  ListState as ErpListState,
  StatusBadge,
  formatCents as money,
} from '../components/shared/Ui';
import './Services.css';
import '../components/erp/Erp.css';
import { queryKeys, staleTimes } from '../config/queryKeys';
import { appAlert } from '../utils/appDialog';
import { companyOptionLabel, matchLegalCompany } from '../utils/legalCompany';
import ListPager from '../components/ListPager';
import ListSearchBar from '../components/ListSearchBar';
import { useClientPager } from '../hooks/useClientPager';

const MAX_VENDOR_CONTACTS = 5;
const MAX_INTERNAL_CONTACTS = 5;

function emptyVendorContact() {
  return {
    source: 'app',
    user_id: '',
    full_name: '',
    email: '',
    title: '',
    workflow_role_code: 'vendor_approver',
    is_internal: false,
    is_primary: false,
  };
}

function emptyInternalContact() {
  return {
    source: 'app',
    user_id: '',
    full_name: '',
    email: '',
    title: '',
    workflow_role_code: 'reviewer',
    is_internal: true,
  };
}

function userLabel(u) {
  const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
  return `${name || u.email}${u.email && name ? ` · ${u.email}` : ''}${u.role ? ` (${u.role})` : ''}`;
}

function contactSource(row) {
  if (row?.source === 'custom' || row?.source === 'app') return row.source;
  return row?.user_id ? 'app' : 'custom';
}

const VENDOR_WORKFLOW_ROLE_CODES = ['vendor_approver', 'signatory'];
const INTERNAL_WORKFLOW_ROLE_CODES = ['reviewer', 'approver', 'signatory'];

function rolesForCodes(all, codes) {
  const want = new Set(codes);
  const filtered = (all || []).filter((r) => want.has(String(r.code || '').toLowerCase()));
  return filtered.length ? filtered : all || [];
}

const EMPTY_VENDOR_FORM = {
  name: '',
  email: '',
  tax_id: '',
  country_code: '',
  currency: 'USD',
  company_id: '',
  stakeholders: [emptyVendorContact()],
  internals: [emptyInternalContact()],
};

function freshVendorForm() {
  return {
    ...EMPTY_VENDOR_FORM,
    stakeholders: [emptyVendorContact()],
    internals: [emptyInternalContact()],
  };
}

function textCompare(a, b) {
  return String(a || '').localeCompare(String(b || ''), undefined, {
    sensitivity: 'base',
    numeric: true,
  });
}

function SortHeader({ label, column, sort, onSort, className }) {
  const active = sort.key === column;
  return (
    <th
      scope="col"
      className={className}
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button type="button" className="erp-sort-btn" onClick={() => onSort(column)}>
        {label}
        {active ? (
          <span className="erp-sort-mark" aria-hidden>
            {sort.dir === 'asc' ? '↑' : '↓'}
          </span>
        ) : null}
      </button>
    </th>
  );
}

function uniqueSorted(values) {
  return [...new Set(values.filter(Boolean))].sort((a, b) => textCompare(a, b));
}

function VendorLinkedDocs({ vendorId, kind }) {
  const { hasPermission } = usePermissions();
  const isQuote = kind === 'quotation';
  const canView = hasPermission(isQuote ? 'erp_vendor_quote_view' : 'erp_bills_view');
  const canCreate = hasPermission(isQuote ? 'erp_vendor_quote_manage' : 'erp_bills_manage');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currencyFilter, setCurrencyFilter] = useState('');
  const [sort, setSort] = useState({ key: 'date', dir: 'desc' });

  const query = useQuery(
    isQuote
      ? queryKeys.vendorQuotations({ vendorId })
      : queryKeys.vendorBills({ vendorId }),
    async () =>
      (
        await api.get(isQuote ? '/buying/quotations' : '/buying/bills', {
          params: { vendor_id: vendorId, limit: 200 },
        })
      ).data,
    { enabled: canView && Boolean(vendorId) }
  );

  const rows = useMemo(
    () => (Array.isArray(query.data) ? query.data : []),
    [query.data]
  );
  const numberKey = isQuote ? 'quotation_number' : 'bill_number';
  const dateKey = isQuote ? 'quotation_date' : 'bill_date';
  const hrefBase = isQuote ? '/buying/quotations' : '/buying/bills';
  const title = isQuote ? 'Quotations' : 'Invoices';
  const noun = isQuote ? 'quotation' : 'invoice';

  const statuses = useMemo(() => uniqueSorted(rows.map((row) => row.status)), [rows]);
  const currencies = useMemo(
    () => uniqueSorted(rows.map((row) => row.currency || 'USD')),
    [rows]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = rows.filter((row) => {
      if (statusFilter && row.status !== statusFilter) return false;
      if (currencyFilter && (row.currency || 'USD') !== currencyFilter) return false;
      if (!q) return true;
      const hay = [
        row[numberKey],
        row.vendor_po_number,
        row.status,
        row.currency,
        row.company_name,
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
    const dir = sort.dir === 'desc' ? -1 : 1;
    const value = (row) => {
      if (sort.key === 'number') return row[numberKey] || '';
      if (sort.key === 'po') return row.vendor_po_number || '';
      if (sort.key === 'currency') return row.currency || '';
      if (sort.key === 'status') return row.status || '';
      if (sort.key === 'total') return Number(row.total_cents) || 0;
      return row[dateKey] || '';
    };
    return [...list].sort((a, b) => {
      const av = value(a);
      const bv = value(b);
      if (typeof av === 'number' || typeof bv === 'number') return (av - bv) * dir;
      return textCompare(av, bv) * dir;
    });
  }, [rows, search, statusFilter, currencyFilter, sort, numberKey, dateKey]);

  const resetKey = `${search}|${statusFilter}|${currencyFilter}|${sort.key}|${sort.dir}`;
  const { pageItems, pagination, setPage } = useClientPager(filtered, { resetKey });

  const toggleSort = (column) => {
    setSort((prev) =>
      prev.key === column
        ? { key: column, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key: column, dir: column === 'date' || column === 'total' ? 'desc' : 'asc' }
    );
  };

  return (
    <section className="erp-panel" aria-label={title}>
      <div className="erp-panel-head">
        <div>
          <h2>{title}</h2>
          <p className="erp-panel-meta">
            {canView
              ? isQuote
                ? `${filtered.length} ${filtered.length === 1 ? 'quotation' : 'quotations'} linked to this vendor`
                : `${filtered.length} ${filtered.length === 1 ? 'invoice' : 'invoices'} (supplier bills) linked to this vendor`
              : `You need ${isQuote ? 'quotation' : 'bill'} access to see these documents.`}
          </p>
        </div>
        {canCreate ? (
          <Link
            className="btn btn-primary btn-sm"
            to={`${hrefBase}/new?vendor_id=${encodeURIComponent(vendorId)}`}
          >
            <FiPlus aria-hidden /> New {noun}
          </Link>
        ) : null}
      </div>
      {canView ? (
        <>
          <div className="list-filters-panel list-toolbar-panel">
            <div className="filter-row list-toolbar-row">
              <div className="filter-group filter-group--wide list-toolbar-search">
                <ListSearchBar
                  id={`vendor-${kind}-search`}
                  value={search}
                  onChange={setSearch}
                  placeholder={`Search ${noun} number, PO, or status…`}
                  label={`Search ${noun}s`}
                />
              </div>
              <div className="filter-group">
                <label htmlFor={`vendor-${kind}-status`}>Status</label>
                <select
                  id={`vendor-${kind}-status`}
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">All statuses</option>
                  {statuses.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label htmlFor={`vendor-${kind}-currency`}>Currency</label>
                <select
                  id={`vendor-${kind}-currency`}
                  value={currencyFilter}
                  onChange={(e) => setCurrencyFilter(e.target.value)}
                >
                  <option value="">All currencies</option>
                  {currencies.map((currency) => (
                    <option key={currency} value={currency}>
                      {currency}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <ErpListState
            query={query}
            isEmpty={filtered.length === 0}
            empty={
              <ErpEmpty
                icon={FiTruck}
                title={rows.length === 0 ? `No ${noun}s yet` : `No ${noun}s match these filters`}
                hint={
                  rows.length === 0
                    ? `No ${noun}s are linked to this vendor yet.`
                    : 'Clear a filter to see the rest of this vendor’s documents.'
                }
              />
            }
          >
            <div className="erp-table-scroll">
              <table className="erp-table">
                <thead>
                  <tr>
                    <SortHeader label="Number" column="number" sort={sort} onSort={toggleSort} />
                    <SortHeader label="Vendor PO" column="po" sort={sort} onSort={toggleSort} />
                    <SortHeader label="Date" column="date" sort={sort} onSort={toggleSort} />
                    <SortHeader label="Currency" column="currency" sort={sort} onSort={toggleSort} />
                    <SortHeader
                      label="Total"
                      column="total"
                      sort={sort}
                      onSort={toggleSort}
                      className="erp-num"
                    />
                    <SortHeader label="Status" column="status" sort={sort} onSort={toggleSort} />
                  </tr>
                </thead>
                <tbody>
                  {pageItems.map((row) => (
                    <tr key={row.id}>
                      <td className="erp-cell-primary">
                        <Link to={`${hrefBase}/${row.id}`}>
                          <span className="erp-code">{row[numberKey]}</span>
                        </Link>
                      </td>
                      <td>
                        {row.vendor_po_id ? (
                          <Link to={`/buying/purchase-orders/${row.vendor_po_id}`}>
                            <span className="erp-code">{row.vendor_po_number || row.vendor_po_id}</span>
                          </Link>
                        ) : (
                          <span className="erp-muted">—</span>
                        )}
                      </td>
                      <td>{row[dateKey] || <span className="erp-muted">—</span>}</td>
                      <td>
                        <span className="erp-code">{row.currency || 'USD'}</span>
                      </td>
                      <td className="erp-num">{money(row.total_cents, row.currency)}</td>
                      <td>
                        <StatusBadge status={row.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ListPager
              page={pagination.page}
              pageCount={pagination.pageCount}
              total={pagination.total}
              limit={pagination.limit}
              onPageChange={setPage}
            />
          </ErpListState>
        </>
      ) : null}
    </section>
  );
}

const Vendors = () => {
  const { hasPermission } = usePermissions();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('id') || '';
  const isCreating = searchParams.get('new') === '1';
  const detailOpen = Boolean(selectedId) || isCreating;
  const [form, setForm] = useState(freshVendorForm);
  const [editingId, setEditingId] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [currencyFilter, setCurrencyFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });

  const backToList = () => {
    setEditingId(null);
    setForm(freshVendorForm());
    setSearchParams({});
  };

  const list = useQuery('erp-vendors', async () => (await api.get('/vendors')).data, {
    enabled: hasPermission('erp_vendors_view') || hasPermission('tab_vendors'),
  });
  const companies = useQuery(
    queryKeys.accountingCompanies,
    async () => (await api.get('/accounting/companies')).data,
    { staleTime: staleTimes.reference, enabled: hasPermission('erp_vendors_view') || hasPermission('tab_vendors') }
  );
  const { data: workflowRoles = [] } = useQuery(
    ['workflow-roles', 'metadata'],
    () => api.get('/workflow-roles').then((r) => r.data || []),
    { staleTime: staleTimes.reference, enabled: hasPermission('erp_vendors_manage') }
  );
  const vendorWorkflowRoles = useMemo(
    () => rolesForCodes(workflowRoles, VENDOR_WORKFLOW_ROLE_CODES),
    [workflowRoles]
  );
  const internalWorkflowRoles = useMemo(
    () => rolesForCodes(workflowRoles, INTERNAL_WORKFLOW_ROLE_CODES),
    [workflowRoles]
  );
  const { data: allUsers = [] } = useQuery(
    queryKeys.usersPicker('vendor-contacts'),
    () =>
      api
        .get('/users')
        .then((r) => (r.data || []).filter((u) => u.is_active && u.role !== 'candidate')),
    { staleTime: staleTimes.lists, enabled: hasPermission('erp_vendors_manage') }
  );
  const staffUsers = useMemo(
    () => (allUsers || []).filter((u) => u.role !== 'external'),
    [allUsers]
  );
  const externalUsers = useMemo(
    () => (allUsers || []).filter((u) => u.role === 'external'),
    [allUsers]
  );

  const combinedStakeholders = () => {
    const vendors = (form.stakeholders || [])
      .filter((s) => (contactSource(s) === 'app' ? s.user_id : s.full_name || s.email))
      .map((s, i) => {
        const u = externalUsers.find((x) => String(x.id) === String(s.user_id));
        const fromApp = contactSource(s) === 'app' && u;
        return {
          user_id: contactSource(s) === 'app' ? s.user_id || null : null,
          full_name: fromApp
            ? [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || s.full_name
            : s.full_name,
          email: fromApp ? u.email || s.email : s.email,
          title: s.title || null,
          workflow_role_code: s.workflow_role_code || null,
          is_internal: 0,
          is_primary: s.is_primary ? 1 : 0,
          sort_order: (i + 1) * 10,
        };
      });
    const internals = (form.internals || [])
      .filter((s) => (contactSource(s) === 'app' ? s.user_id : s.full_name || s.email))
      .map((s, i) => {
        const u = staffUsers.find((x) => String(x.id) === String(s.user_id));
        const fromApp = contactSource(s) === 'app' && u;
        return {
          user_id: contactSource(s) === 'app' ? s.user_id || null : null,
          full_name: fromApp
            ? [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || s.full_name
            : s.full_name,
          email: fromApp ? u.email || s.email : s.email,
          title: s.title || null,
          workflow_role_code: s.workflow_role_code || null,
          is_internal: 1,
          is_primary: 0,
          sort_order: (i + 1) * 10,
        };
      });
    return [...vendors, ...internals];
  };

  const patchStakeholders = (idx, patch) => {
    const stakeholders = [...(form.stakeholders || [])];
    stakeholders[idx] = { ...stakeholders[idx], ...patch };
    if (patch.is_primary) {
      for (let i = 0; i < stakeholders.length; i += 1) {
        if (i !== idx) stakeholders[i] = { ...stakeholders[i], is_primary: false };
      }
    }
    setForm({ ...form, stakeholders });
  };

  const patchInternals = (idx, patch) => {
    const internals = [...(form.internals || [])];
    internals[idx] = { ...internals[idx], ...patch };
    setForm({ ...form, internals });
  };

  const selectContactUser = (kind, idx, userId, users) => {
    const patch = kind === 'vendor' ? patchStakeholders : patchInternals;
    if (!userId) {
      patch(idx, { user_id: '', full_name: '', email: '' });
      return;
    }
    const u = (users || []).find((x) => String(x.id) === String(userId));
    if (!u) {
      patch(idx, { user_id: userId });
      return;
    }
    patch(idx, {
      source: 'app',
      user_id: String(u.id),
      full_name: [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.email || '',
      email: u.email || '',
    });
  };

  const setContactSource = (kind, idx, source) => {
    const patch = kind === 'vendor' ? patchStakeholders : patchInternals;
    if (source === 'custom') {
      patch(idx, { source: 'custom', user_id: '' });
      return;
    }
    patch(idx, { source: 'app' });
  };

  const vendorPayload = () => {
    const matched = matchLegalCompany(companies.data, {
      companyId: form.company_id,
      countryCode: form.country_code,
      currency: form.currency,
    });
    const body = {
      name: form.name,
      email: form.email || null,
      country_code: form.country_code ? form.country_code.toUpperCase() : '',
      currency: (form.currency || 'USD').toUpperCase(),
      company_id: form.company_id || matched?.id || undefined,
    };
    if (form.tax_id.trim()) body.tax_id = form.tax_id.trim();
    body.stakeholders = combinedStakeholders();
    return body;
  };

  const create = useMutation(async () => (await api.post('/vendors', vendorPayload())).data, {
    onSuccess: (row) => {
      qc.invalidateQueries('erp-vendors');
      if (row?.id) setSearchParams({ id: String(row.id) });
      else setSearchParams({});
    },
    onError: (e) => void appAlert(e?.response?.data?.error || e.message),
  });

  const update = useMutation(
    async () => (await api.put(`/vendors/${editingId}`, vendorPayload())).data,
    {
      onSuccess: () => {
        qc.invalidateQueries('erp-vendors');
      },
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );

  useEffect(() => {
    if (isCreating || !selectedId) {
      setEditingId(null);
      setForm(freshVendorForm());
      setDetailLoading(false);
      return undefined;
    }
    let cancelled = false;
    setEditingId(selectedId);
    setDetailLoading(true);
    setForm((prev) => ({
      ...freshVendorForm(),
      name: prev.name,
    }));
    api
      .get(`/vendors/${selectedId}`)
      .then((r) => {
        if (cancelled) return;
        const detail = r.data || {};
        const all = detail.stakeholders || [];
        const vendors = all.filter((s) => !s.is_internal);
        const internals = all.filter((s) => s.is_internal);
        setForm({
          name: detail.name || '',
          email: detail.email || '',
          tax_id: '',
          country_code: detail.country_code || '',
          currency: detail.currency || 'USD',
          company_id: detail.company_id || '',
          stakeholders: vendors.length
            ? vendors.map((s) => ({
                source: s.user_id ? 'app' : 'custom',
                user_id: s.user_id || '',
                full_name: s.full_name || '',
                email: s.email || s.user_email || '',
                title: s.title || '',
                workflow_role_code: s.workflow_role_code || '',
                is_internal: false,
                is_primary: Boolean(s.is_primary),
              }))
            : [emptyVendorContact()],
          internals: internals.length
            ? internals.map((s) => ({
                source: s.user_id ? 'app' : 'custom',
                user_id: s.user_id || '',
                full_name: s.full_name || '',
                email: s.email || s.user_email || '',
                title: s.title || '',
                workflow_role_code: s.workflow_role_code || '',
                is_internal: true,
              }))
            : [emptyInternalContact()],
        });
      })
      .catch((e) => {
        if (!cancelled) void appAlert(e?.response?.data?.error || e.message);
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId, isCreating]);

  const rows = useMemo(() => list.data || [], [list.data]);
  const countries = useMemo(() => uniqueSorted(rows.map((v) => v.country_code)), [rows]);
  const currencies = useMemo(
    () => uniqueSorted(rows.map((v) => v.currency || 'USD')),
    [rows]
  );
  const companiesInList = useMemo(() => {
    const byId = new Map();
    rows.forEach((v) => {
      if (!v.company_id) return;
      if (!byId.has(String(v.company_id))) {
        byId.set(String(v.company_id), v.company_name || v.company_id);
      }
    });
    return [...byId.entries()].sort((a, b) => textCompare(a[1], b[1]));
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const listRows = rows.filter((v) => {
      if (countryFilter && (v.country_code || '') !== countryFilter) return false;
      if (currencyFilter && (v.currency || 'USD') !== currencyFilter) return false;
      if (companyFilter && String(v.company_id || '') !== companyFilter) return false;
      if (!q) return true;
      const hay = [v.name, v.email, v.company_name, v.country_code, v.currency]
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
    const dir = sort.dir === 'desc' ? -1 : 1;
    const value = (v) => {
      if (sort.key === 'email') return v.email || '';
      if (sort.key === 'country') return v.country_code || '';
      if (sort.key === 'currency') return v.currency || '';
      if (sort.key === 'company') return v.company_name || '';
      return v.name || '';
    };
    return [...listRows].sort((a, b) => textCompare(value(a), value(b)) * dir);
  }, [rows, search, countryFilter, currencyFilter, companyFilter, sort]);

  const toggleSort = (column) => {
    setSort((prev) =>
      prev.key === column
        ? { key: column, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key: column, dir: 'asc' }
    );
  };
  const canReveal = hasPermission('erp_sensitive_reveal');
  const canManage = hasPermission('erp_vendors_manage');
  const vendorResetKey = `${search}|${countryFilter}|${currencyFilter}|${companyFilter}|${sort.key}|${sort.dir}`;
  const {
    pageItems: pagedRows,
    pagination: vendorPagination,
    setPage: setVendorPage,
  } = useClientPager(filteredRows, { resetKey: vendorResetKey });

  if (!hasPermission('erp_vendors_view') && !hasPermission('tab_vendors')) {
    return <div className="error" role="alert">No vendor access.</div>;
  }

  return (
    <div className="erp-page list-page">
      <FadeIn className="page-header">
        <div className="page-header-text">
          <h1><FiTruck aria-hidden /> Vendors</h1>
          <p className="page-subtitle">
            {detailOpen
              ? isCreating
                ? 'Add a supplier, then raise quotations and invoices against it.'
                : 'Vendor details, plus the quotations and invoices linked to this supplier.'
              : 'Suppliers already on file. Open a vendor to see its details, quotations, and invoices.'}
          </p>
        </div>
        <div className="page-toolbar">
          {detailOpen ? (
            <button type="button" className="btn btn-secondary" onClick={backToList}>
              <FiArrowLeft aria-hidden /> Back to list
            </button>
          ) : canManage ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setSearchParams({ new: '1' })}
            >
              <FiPlus aria-hidden /> Add vendor
            </button>
          ) : null}
        </div>
      </FadeIn>

      {detailOpen && (
        <section className="erp-panel erp-panel--form">
          <div className="erp-panel-head">
            <div>
              <h2>{isCreating ? 'New vendor' : form.name || 'Vendor details'}</h2>
              <p className="erp-panel-meta">
                {detailLoading
                  ? 'Loading vendor…'
                  : 'Country and currency choose the legal company GL.'}
              </p>
            </div>
          </div>
          <fieldset className="erp-fieldset-plain" disabled={!canManage || detailLoading}>
          <div className="erp-form-grid">
            <div className="erp-field">
              <label htmlFor="vendor-name">Name</label>
              <input
                id="vendor-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Acme Supplies Ltd"
              />
            </div>
            <div className="erp-field">
              <label htmlFor="vendor-email">Email</label>
              <input
                id="vendor-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="ap@acme.com"
              />
            </div>
            <div className="erp-field">
              <label htmlFor="vendor-country">Country</label>
              <input
                id="vendor-country"
                value={form.country_code}
                onChange={(e) =>
                  setForm({ ...form, country_code: e.target.value.toUpperCase() })
                }
                placeholder="US"
                maxLength={8}
              />
            </div>
            <div className="erp-field">
              <label htmlFor="vendor-currency">Currency</label>
              <input
                id="vendor-currency"
                value={form.currency}
                onChange={(e) =>
                  setForm({ ...form, currency: e.target.value.toUpperCase() })
                }
                placeholder="USD"
                maxLength={3}
              />
            </div>
            <div className="erp-field">
              <label htmlFor="vendor-company">Legal company</label>
              <select
                id="vendor-company"
                value={form.company_id}
                onChange={(e) => setForm({ ...form, company_id: e.target.value })}
              >
                <option value="">Match country + currency</option>
                {(companies.data || []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {companyOptionLabel(c)}
                  </option>
                ))}
              </select>
            </div>
            <div className="erp-field">
              <label htmlFor="vendor-tax-id">Tax ID</label>
              <input
                id="vendor-tax-id"
                value={form.tax_id}
                onChange={(e) => setForm({ ...form, tax_id: e.target.value })}
                placeholder={editingId ? 'Leave blank to keep existing' : ''}
              />
            </div>
            <div className="erp-field" style={{ gridColumn: '1 / -1' }}>
              <label>Vendor contacts</label>
              <p className="form-hint">
                Vendor-side people for buying documents. Choose App user to pick an existing
                external user, or Custom to enter name and email. Assign Vendor approver so they
                are asked after Internal review and Internal approval. Signatory is snapshotted onto
                POs, quotations, and bills. At most {MAX_VENDOR_CONTACTS}.
              </p>
              {(form.stakeholders || []).map((s, idx) => {
                const source = contactSource(s);
                return (
                  <div key={`vc-${idx}`} className="svc-customer-stakeholder-row">
                    <div className="erp-form-grid">
                      <div className="erp-field">
                        <label htmlFor={`vc-type-${idx}`}>Type</label>
                        <select
                          id={`vc-type-${idx}`}
                          value={source}
                          onChange={(e) => setContactSource('vendor', idx, e.target.value)}
                        >
                          <option value="app">App user</option>
                          <option value="custom">Custom</option>
                        </select>
                      </div>
                      {source === 'app' ? (
                        <div className="erp-field">
                          <label htmlFor={`vc-user-${idx}`}>App user</label>
                          <select
                            id={`vc-user-${idx}`}
                            value={s.user_id || ''}
                            onChange={(e) =>
                              selectContactUser('vendor', idx, e.target.value, externalUsers)
                            }
                          >
                            <option value="">Select external user…</option>
                            {externalUsers.map((u) => (
                              <option key={u.id} value={String(u.id)}>
                                {userLabel(u)}
                              </option>
                            ))}
                          </select>
                        </div>
                      ) : (
                        <>
                          <div className="erp-field">
                            <label htmlFor={`vc-name-${idx}`}>Name</label>
                            <input
                              id={`vc-name-${idx}`}
                              value={s.full_name}
                              onChange={(e) => patchStakeholders(idx, { full_name: e.target.value })}
                              placeholder="Jane Doe"
                            />
                          </div>
                          <div className="erp-field">
                            <label htmlFor={`vc-email-${idx}`}>Email</label>
                            <input
                              id={`vc-email-${idx}`}
                              type="email"
                              value={s.email}
                              onChange={(e) => patchStakeholders(idx, { email: e.target.value })}
                            />
                          </div>
                          <div className="erp-field">
                            <label htmlFor={`vc-title-${idx}`}>Title</label>
                            <input
                              id={`vc-title-${idx}`}
                              value={s.title}
                              onChange={(e) => patchStakeholders(idx, { title: e.target.value })}
                              placeholder="Accounts payable"
                            />
                          </div>
                        </>
                      )}
                      <div className="erp-field">
                        <label htmlFor={`vc-role-${idx}`}>Workflow role</label>
                        <select
                          id={`vc-role-${idx}`}
                          value={s.workflow_role_code || ''}
                          onChange={(e) =>
                            patchStakeholders(idx, { workflow_role_code: e.target.value })
                          }
                        >
                          <option value="">— None —</option>
                          {vendorWorkflowRoles.map((r) => (
                            <option key={r.code} value={r.code}>
                              {r.name}
                            </option>
                          ))}
                          {!vendorWorkflowRoles.some(
                            (r) => String(r.code).toLowerCase() === 'vendor_approver'
                          ) ? (
                            <option value="vendor_approver">Vendor approver</option>
                          ) : null}
                        </select>
                      </div>
                      <div className="erp-field">
                        <label>
                          <input
                            type="checkbox"
                            checked={Boolean(s.is_primary)}
                            onChange={() => patchStakeholders(idx, { is_primary: true })}
                          />{' '}
                          Primary
                        </label>
                        {form.stakeholders.length > 1 ? (
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            aria-label={`Remove vendor contact ${idx + 1}`}
                            onClick={() =>
                              setForm({
                                ...form,
                                stakeholders: form.stakeholders.filter((_, i) => i !== idx),
                              })
                            }
                          >
                            <FiTrash2 />
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              })}
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={(form.stakeholders || []).length >= MAX_VENDOR_CONTACTS}
                onClick={() =>
                  setForm({ ...form, stakeholders: [...form.stakeholders, emptyVendorContact()] })
                }
              >
                <FiPlus /> Add vendor contact
              </button>
            </div>
            <div className="erp-field" style={{ gridColumn: '1 / -1' }}>
              <label>Internal contacts</label>
              <p className="form-hint">
                Staff who review or approve this vendor&apos;s documents. Choose App user to pick an
                internal user, or Custom to enter details manually. Reviewer is notified at Internal
                review; Approver at Internal approval. Prefer App user so they can approve in the
                app. At most {MAX_INTERNAL_CONTACTS}.
              </p>
              {(form.internals || []).map((s, idx) => {
                const source = contactSource(s);
                return (
                  <div key={`vi-${idx}`} className="svc-customer-stakeholder-row">
                    <div className="erp-form-grid">
                      <div className="erp-field">
                        <label htmlFor={`vi-type-${idx}`}>Type</label>
                        <select
                          id={`vi-type-${idx}`}
                          value={source}
                          onChange={(e) => setContactSource('internal', idx, e.target.value)}
                        >
                          <option value="app">App user</option>
                          <option value="custom">Custom</option>
                        </select>
                      </div>
                      {source === 'app' ? (
                        <div className="erp-field">
                          <label htmlFor={`vi-user-${idx}`}>App user</label>
                          <select
                            id={`vi-user-${idx}`}
                            aria-label="Internal app user"
                            value={s.user_id || ''}
                            onChange={(e) =>
                              selectContactUser('internal', idx, e.target.value, staffUsers)
                            }
                          >
                            <option value="">Select staff user…</option>
                            {staffUsers.map((u) => (
                              <option key={u.id} value={String(u.id)}>
                                {userLabel(u)}
                              </option>
                            ))}
                          </select>
                        </div>
                      ) : (
                        <>
                          <div className="erp-field">
                            <label htmlFor={`vi-name-${idx}`}>Name</label>
                            <input
                              id={`vi-name-${idx}`}
                              value={s.full_name}
                              onChange={(e) => patchInternals(idx, { full_name: e.target.value })}
                            />
                          </div>
                          <div className="erp-field">
                            <label htmlFor={`vi-email-${idx}`}>Email</label>
                            <input
                              id={`vi-email-${idx}`}
                              type="email"
                              value={s.email}
                              onChange={(e) => patchInternals(idx, { email: e.target.value })}
                            />
                          </div>
                          <div className="erp-field">
                            <label htmlFor={`vi-title-${idx}`}>Title</label>
                            <input
                              id={`vi-title-${idx}`}
                              value={s.title}
                              onChange={(e) => patchInternals(idx, { title: e.target.value })}
                            />
                          </div>
                        </>
                      )}
                      <div className="erp-field">
                        <label htmlFor={`vi-role-${idx}`}>Workflow role</label>
                        <select
                          id={`vi-role-${idx}`}
                          value={s.workflow_role_code || ''}
                          onChange={(e) =>
                            patchInternals(idx, { workflow_role_code: e.target.value })
                          }
                        >
                          <option value="">— None —</option>
                          {internalWorkflowRoles.map((r) => (
                            <option key={r.code} value={r.code}>
                              {r.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      {form.internals.length > 1 ? (
                        <div className="erp-field">
                          <label>&nbsp;</label>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            aria-label={`Remove internal contact ${idx + 1}`}
                            onClick={() =>
                              setForm({
                                ...form,
                                internals: form.internals.filter((_, i) => i !== idx),
                              })
                            }
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={(form.internals || []).length >= MAX_INTERNAL_CONTACTS}
                onClick={() =>
                  setForm({ ...form, internals: [...form.internals, emptyInternalContact()] })
                }
              >
                <FiPlus /> Add internal contact
              </button>
            </div>
            {canManage ? (
            <div className="erp-form-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => (editingId ? update.mutate() : create.mutate())}
                disabled={!form.name || create.isLoading || update.isLoading}
              >
                {editingId ? (
                  <>
                    <FiEdit2 aria-hidden /> Save changes
                  </>
                ) : (
                  <>
                    <FiPlus aria-hidden /> Add vendor
                  </>
                )}
              </button>
            </div>
            ) : null}
          </div>
          </fieldset>
        </section>
      )}

      {selectedId ? (
        <>
          <VendorLinkedDocs vendorId={selectedId} kind="quotation" />
          <VendorLinkedDocs vendorId={selectedId} kind="invoice" />
        </>
      ) : null}

      {!detailOpen && (
      <section className="erp-panel">
        <div className="erp-panel-head">
          <h2>Vendors</h2>
          <p className="erp-panel-meta">
            {filteredRows.length} of {rows.length} {rows.length === 1 ? 'vendor' : 'vendors'}
          </p>
        </div>
        <div className="list-filters-panel list-toolbar-panel">
          <div className="filter-row list-toolbar-row">
            <div className="filter-group filter-group--wide list-toolbar-search">
              <ListSearchBar
                id="vendor-search"
                value={search}
                onChange={setSearch}
                placeholder="Search name, email, or company…"
                label="Search vendors"
              />
            </div>
            <div className="filter-group">
              <label htmlFor="vendor-filter-country">Country</label>
              <select
                id="vendor-filter-country"
                value={countryFilter}
                onChange={(e) => setCountryFilter(e.target.value)}
              >
                <option value="">All countries</option>
                {countries.map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label htmlFor="vendor-filter-currency">Currency</label>
              <select
                id="vendor-filter-currency"
                value={currencyFilter}
                onChange={(e) => setCurrencyFilter(e.target.value)}
              >
                <option value="">All currencies</option>
                {currencies.map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label htmlFor="vendor-filter-company">Company</label>
              <select
                id="vendor-filter-company"
                value={companyFilter}
                onChange={(e) => setCompanyFilter(e.target.value)}
              >
                <option value="">All companies</option>
                {companiesInList.map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <ErpListState
          query={list}
          isEmpty={filteredRows.length === 0}
          empty={
            <ErpEmpty
              icon={FiTruck}
              title={rows.length === 0 ? 'No vendors yet' : 'No vendors match these filters'}
              hint={
                rows.length === 0
                  ? 'Add a supplier to raise vendor purchase orders, quotations, and invoices against it.'
                  : 'Clear a filter or search to see the rest of the vendor list.'
              }
            />
          }
        >
          <div className="erp-table-scroll">
            <table className="erp-table">
              <thead>
                <tr>
                  <SortHeader label="Name" column="name" sort={sort} onSort={toggleSort} />
                  <SortHeader label="Email" column="email" sort={sort} onSort={toggleSort} />
                  <SortHeader label="Country" column="country" sort={sort} onSort={toggleSort} />
                  <SortHeader label="Currency" column="currency" sort={sort} onSort={toggleSort} />
                  <SortHeader label="Company" column="company" sort={sort} onSort={toggleSort} />
                  <th scope="col">Tax ID</th>
                </tr>
              </thead>
              <tbody>
                {pagedRows.map((v) => (
                  <tr
                    key={v.id}
                    className="erp-row-link"
                    tabIndex={0}
                    role="link"
                    aria-label={`Open vendor ${v.name}`}
                    onClick={() => setSearchParams({ id: String(v.id) })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSearchParams({ id: String(v.id) });
                      }
                    }}
                  >
                    <td className="erp-cell-primary">{v.name}</td>
                    <td>{v.email || <span className="erp-muted">—</span>}</td>
                    <td>{v.country_code || <span className="erp-muted">—</span>}</td>
                    <td><span className="erp-code">{v.currency || 'USD'}</span></td>
                    <td>{v.company_name || <span className="erp-muted">—</span>}</td>
                    <td onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                      <SensitiveField
                        entityType="vendor"
                        entityId={v.id}
                        field="tax_id"
                        masked={v.tax_id_masked}
                        canReveal={canReveal && v.has_tax_id}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ListPager
            page={vendorPagination.page}
            pageCount={vendorPagination.pageCount}
            total={vendorPagination.total}
            limit={vendorPagination.limit}
            onPageChange={setVendorPage}
          />
        </ErpListState>
      </section>
      )}
    </div>
  );
};

export default Vendors;
